import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Visitor } from '../../shared/api/types';
import { Alert, Button, Card, Field } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney, parseMoney } from '../finance/money';
import { useVisitorAccess } from '../visitors/shared';
import { VisitorForm } from '../visitors/VisitorForm';
import { personOptions, SearchSelect } from '../../shared/ui/SearchSelect';

type Offerings = {
  canCapture: boolean;
  /** Solo el pastor y la administración ven a nombre de quién se dio. */
  canSeeNames: boolean;
  accounts: { id: string; name: string; currency: string }[];
  givers: { people: { personId: string; name: string }[]; families: { id: string; name: string }[] };
  items: { id: string; category: string; amountCents: number; description: string | null; account: string; reversed: boolean; recordedAt: string; named: boolean; giver: string | null }[];
  totals: { diezmos: number; ofrendas: number; otros: number; total: number };
};

/** Finanzas en el culto: diezmos y ofrendas que entran directo a Finanzas, ligados a este culto. */
export function OfferingsCard({ churchId, serviceId }: { churchId: string; serviceId: string }) {
  const url = `/finance/churches/${churchId}/services/${serviceId}/offerings`;
  const data = useAsync((signal) => api.get<Offerings>(url, signal).catch(() => null), [url]);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [giverKind, setGiverKind] = useState<'nadie' | 'persona' | 'familia'>('nadie');
  if (data.status !== 'ready' || !data.data?.canCapture) return null;
  const o = data.data;
  const currency = o.accounts[0]?.currency ?? 'MXN';
  const money = (c: number) => formatMoney(c, currency);
  async function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    const amountCents = parseMoney(String(f.get('amount') ?? ''));
    if (!amountCents) return setMsg({ tone: 'error', text: 'Escribe la cantidad.' });
    setBusy(true);
    setMsg(null);
    try {
      const giver = String(f.get('giver') ?? '') || null;
      if (giverKind !== 'nadie' && !giver) {
        setBusy(false);
        return setMsg({ tone: 'error', text: giverKind === 'persona' ? 'Elige a la persona.' : 'Elige a la familia.' });
      }
      await api.post(url, {
        type: String(f.get('type')),
        amountCents,
        accountId: String(f.get('accountId')),
        personId: giverKind === 'persona' ? giver : null,
        familyId: giverKind === 'familia' ? giver : null,
        description: String(f.get('description') ?? '').trim() || null,
      });
      setMsg({ tone: 'success', text: 'Capturado. Ya está en Finanzas.' });
      form.reset();
      setGiverKind('nadie');
      data.reload();
    } catch (err) {
      setMsg({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card title="Diezmos y ofrendas del culto">
      <dl className="details">
        <dt>Diezmos</dt>
        <dd>{money(o.totals.diezmos)}</dd>
        <dt>Ofrendas</dt>
        <dd>{money(o.totals.ofrendas)}</dd>
        <dt>Total</dt>
        <dd>
          <strong>{money(o.totals.total)}</strong>
        </dd>
      </dl>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {o.accounts.length === 0 ? (
        <p className="muted small">
          Primero crea una cuenta (por ejemplo «Caja») en <Link to="/app/finanzas">Finanzas</Link>.
        </p>
      ) : (
        <form className="form" onSubmit={add} noValidate>
          <div className="form__row form__row--3">
            <div className="field">
              <label htmlFor="off-type">Tipo</label>
              <select id="off-type" name="type" defaultValue="ofrenda">
                <option value="ofrenda">Ofrenda</option>
                <option value="diezmo">Diezmo</option>
              </select>
            </div>
            <Field label="Cantidad" name="amount" inputMode="decimal" required placeholder="0.00" />
            <div className="field">
              <label htmlFor="off-account">Cuenta</label>
              <select id="off-account" name="accountId" defaultValue={o.accounts[0]!.id}>
                {o.accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form__row">
            <div className="field">
              <label htmlFor="off-giver-kind">A nombre de</label>
              <select id="off-giver-kind" value={giverKind} onChange={(e) => setGiverKind(e.target.value as typeof giverKind)}>
                <option value="nadie">Sin nombre</option>
                <option value="persona">Una persona</option>
                <option value="familia">Una familia</option>
              </select>
            </div>
            {giverKind === 'persona' ? (
              <div className="field">
                <label htmlFor="off-giver">Persona</label>
                <SearchSelect id="off-giver" name="giver" options={personOptions(o.givers.people)} />
              </div>
            ) : null}
            {giverKind === 'familia' ? (
              <div className="field">
                <label htmlFor="off-giver">Familia</label>
                <select id="off-giver" name="giver" defaultValue="">
                  <option value="">Elige…</option>
                  {o.givers.families.map((x) => (
                    <option key={x.id} value={x.id}>
                      Familia {x.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
          {giverKind !== 'nadie' ? <p className="muted small">A nombre de quién se dio solo lo ven el pastor y la administración, para su estadística.</p> : null}
          <Field label="Nota (opcional)" name="description" maxLength={300} placeholder="Ej. sobres, efectivo, transferencia" />
          <Button type="submit" busy={busy}>
            Capturar
          </Button>
        </form>
      )}
      {o.items.length ? (
        <ul className="list">
          {o.items.map((i) => (
            <li key={i.id} className="list__item">
              <span>
                {i.category} · {i.account}
                {i.giver ? <span className="small"> · {i.giver}</span> : i.named ? <span className="small muted"> · con nombre</span> : null}
                {i.description ? <span className="small muted"> · {i.description}</span> : null}
                {i.reversed ? <span className="badge badge--warn"> Revertido</span> : null}
              </span>
              <strong>{money(i.amountCents)}</strong>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

type Door = {
  visible: boolean;
  canAssign: boolean;
  ministry: { id: string; name: string } | null;
  assigned: { id: string; personId: string; name: string; role: string; status: string }[];
  candidates: { personId: string; name: string }[];
};

/** Bienvenida en el culto: registra a quienes llegan por primera vez y ve quién está en la puerta (la asigna el líder de Bienvenida). */
export function WelcomeCard({ churchId, serviceId }: { churchId: string; serviceId: string }) {
  const access = useVisitorAccess(churchId);
  const visitors = useAsync(
    (signal) => (access.canUse ? api.get<{ items: Visitor[] }>(`/multichurch/churches/${churchId}/visitors`, signal).then((r) => r.items.filter((v) => v.serviceId === serviceId)) : Promise.resolve([])),
    [churchId, serviceId, access.canUse],
  );
  const doorUrl = `/assignments/churches/${churchId}/services/${serviceId}/door`;
  const door = useAsync((signal) => api.get<Door>(doorUrl, signal).catch(() => null), [doorUrl]);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const d = door.status === 'ready' ? door.data : null;
  if (!access.canUse && !d?.visible) return null;
  const list = visitors.status === 'ready' ? visitors.data : [];
  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      door.reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  const free = d ? d.candidates.filter((c) => !d.assigned.some((a) => a.personId === c.personId)) : [];
  return (
    <Card title="Bienvenida">
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {error ? <Alert>{error}</Alert> : null}
      {d?.visible ? (
        <div className="small">
          <strong>En la puerta:</strong>{' '}
          {d.assigned.length === 0 ? <span className="muted">nadie asignado</span> : null}
          {d.assigned.map((a, i) => (
            <span key={a.id}>
              {i ? ', ' : ''}
              {a.name}
              {a.status === 'requested' ? <span className="muted"> (sin confirmar)</span> : null}
              {d.canAssign ? (
                <button type="button" className="link-button" aria-label={`Quitar a ${a.name} de la puerta`} disabled={busy} onClick={() => void run(() => api.post(`/assignments/churches/${churchId}/assignments/${a.id}/cancel`))}>
                  {' '}×
                </button>
              ) : null}
            </span>
          ))}
          {d.canAssign ? (
            free.length ? (
              <form
                className="actions actions--wrap"
                onSubmit={(e) => {
                  e.preventDefault();
                  const personId = String(new FormData(e.currentTarget).get('personId') ?? '');
                  if (personId) void run(() => api.post(doorUrl, { personId }));
                }}
              >
                <SearchSelect name="personId" aria-label="Quién estará en la puerta" placeholder={`Busca a alguien de ${d.ministry?.name ?? 'Bienvenida'}…`} options={personOptions(free)} />
                <Button type="submit" variant="secondary" busy={busy}>
                  Asignar a la puerta
                </Button>
              </form>
            ) : d.candidates.length === 0 ? (
              <p className="muted">Agrega integrantes al ministerio de {d.ministry?.name ?? 'Bienvenida'} para asignar la puerta.</p>
            ) : null
          ) : null}
        </div>
      ) : null}
      {access.canUse ? (
        <>
          <p className="small">
            <strong>Visitas de este culto:</strong> {list.length ? list.map((v) => `${v.givenName} ${v.familyName}`.trim()).join(', ') : <span className="muted">ninguna todavía</span>}
          </p>
          {adding ? (
            <VisitorForm
              submitLabel="Registrar visita"
              onSubmit={async (v) => {
                await api.post(`/multichurch/churches/${churchId}/visitors`, { ...v, serviceId });
                setAdding(false);
                setNotice(`¡Bienvenido ${v.givenName}! Quedó registrado en Visitas.`);
                visitors.reload();
              }}
            />
          ) : (
            <Button variant="secondary" onClick={() => setAdding(true)}>
              Registrar visita o nuevo
            </Button>
          )}
        </>
      ) : null}
    </Card>
  );
}
